import { AxiosError } from 'axios'
import type { PayloadAction } from '@reduxjs/toolkit'
// eslint-disable-next-line no-duplicate-imports
import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import {
  initialReviewListCycle,
  outgoingInitialGetReviewListProperties,
  reviewRatingsList,
} from '../../../pages/Performance/ReviewList/ReviewListHelpers'
import {
  IncomingReviewList,
  IncomingReviewListCycle,
  IncomingReviewListDesignation,
  OutgoingGetReviewListProps,
  RatingCheckEnum,
  ReviewListDateEnum,
  ReviewListSliceState,
} from '../../../types/Performance/ReviewList/reviewListTypes'
import ReviewListApi from '../../../middleware/api/Performance/ReviewList/ReviewListApi1'
import ChecklistApi from '../../../middleware/api/Checklist/ChecklistApi'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import { RootState } from '../../../stateStore'

const initialReviewListSlice: ReviewListSliceState = {
  isLoading: ApiLoadingState.idle,
  error: null,
  activeCycle: initialReviewListCycle,
  allCycles: [],
  empDepartments: [],
  incomingReviewList: { size: 0, list: [] },
  incomingDesignations: [],
  outgoingReviewListProperties: outgoingInitialGetReviewListProperties,
  appraisalStatus: '',
  selectedItemID: '',
  isView: false,
  isCall: false,
}

const getActiveCycleThunk = createAsyncThunk(
  'ReviewList/getActiveCycleThunk',
  async (_, thunkApi) => {
    try {
      return await ReviewListApi.getActiveCycle()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const getAllCyclesThunk = createAsyncThunk(
  'ReviewList/getAllCyclesThunk',
  async (_, thunkApi) => {
    try {
      return await ReviewListApi.getCycles()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const getEmpDepartmentsThunk = createAsyncThunk(
  'ReviewList/getEmpDepartmentsThunk',
  async (_, thunkApi) => {
    try {
      return await ChecklistApi.getEmpDepartments()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const getDesignationsThunk = createAsyncThunk(
  'ReviewList/getDesignationsThunk',
  async (deptId: number, thunkApi) => {
    try {
      return await ReviewListApi.getDesignations(deptId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const getReviewListThunk = createAsyncThunk(
  'ReviewList/getReviewListThunk',
  async (finalData: OutgoingGetReviewListProps, thunkApi) => {
    try {
      return await ReviewListApi.getReviewList(finalData)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const reviewListSlice = createSlice({
  name: 'ReviewList',
  initialState: initialReviewListSlice,
  reducers: {
    setDesignationList: (
      state,
      action: PayloadAction<IncomingReviewListDesignation[]>,
    ) => {
      state.incomingDesignations = action.payload
    },
    setAppraisalStatus: (state, action: PayloadAction<string>) => {
      state.appraisalStatus = action.payload
    },
    clearReviewListFilterAndTable: (state) => {
      state.incomingReviewList = { size: 0, list: [] }
      state.outgoingReviewListProperties =
        outgoingInitialGetReviewListProperties
    },

    setCycleIdForGetReviewList: (
      state,
      action: PayloadAction<number | string>,
    ) => {
      state.outgoingReviewListProperties = {
        ...state.outgoingReviewListProperties,
        cycleId: action.payload,
      }
    },
    setEmpIdForGetReviewList: (state, action: PayloadAction<number>) => {
      state.outgoingReviewListProperties = {
        ...state.outgoingReviewListProperties,
        employeeID: action.payload,
      }
    },
    setRatingForGetReviewList: (
      state,
      action: PayloadAction<{
        type: RatingCheckEnum
        value: string
        indexVal: number
      }>,
    ) => {
      const { type, value, indexVal } = action.payload
      if (type === RatingCheckEnum.checkAll.toString()) {
        state.outgoingReviewListProperties = {
          ...state.outgoingReviewListProperties,
          ratings: reviewRatingsList,
        }
      } else if (type === RatingCheckEnum.uncheckAll.toString()) {
        state.outgoingReviewListProperties = {
          ...state.outgoingReviewListProperties,
          ratings: [],
        }
      } else if (type === RatingCheckEnum.checkNum.toString()) {
        state.outgoingReviewListProperties.ratings.push(value)
      } else {
        state.outgoingReviewListProperties.ratings.splice(indexVal, 1)
      }
    },
    setDepartmentNameForGetReviewList: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.outgoingReviewListProperties = {
        ...state.outgoingReviewListProperties,
        departmentName: action.payload,
      }
    },
    setDesignationNameForGetReviewList: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.outgoingReviewListProperties = {
        ...state.outgoingReviewListProperties,
        designationName: action.payload,
      }
    },
    setAppraisalStatusForGetReviewList: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.outgoingReviewListProperties = {
        ...state.outgoingReviewListProperties,
        appraisalFormStatus: action.payload,
      }
    },
    setEmpStatusForGetReviewList: (state, action: PayloadAction<string>) => {
      state.outgoingReviewListProperties = {
        ...state.outgoingReviewListProperties,
        empStatus: action.payload,
      }
    },
    setEmploymentTypeForGetReviewList: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.outgoingReviewListProperties = {
        ...state.outgoingReviewListProperties,
        employmentType: action.payload,
      }
    },
    setCountryForGetReviewList: (state, action: PayloadAction<string>) => {
      state.outgoingReviewListProperties = {
        ...state.outgoingReviewListProperties,
        country: action.payload,
      }
    },
    setDateForGetReviewList: (
      state,
      action: PayloadAction<{ type: ReviewListDateEnum; value: string | null }>,
    ) => {
      const { type, value } = action.payload
      if (type === ReviewListDateEnum.fromDate.toString()) {
        state.outgoingReviewListProperties = {
          ...state.outgoingReviewListProperties,
          fromDate: value,
        }
      } else {
        state.outgoingReviewListProperties = {
          ...state.outgoingReviewListProperties,
          toDate: value,
        }
      }
    },
    setRoleForGetReviewList: (state, action: PayloadAction<string>) => {
      state.outgoingReviewListProperties = {
        ...state.outgoingReviewListProperties,
        role: action.payload,
      }
    },
    setSearchInputForGetReviewList: (state, action: PayloadAction<string>) => {
      state.outgoingReviewListProperties = {
        ...state.outgoingReviewListProperties,
        searchString: action.payload,
      }
    },
    setStartEndIndexForGetReviewList: (
      state,
      action: PayloadAction<{ startIndex: number; endIndex: number }>,
    ) => {
      state.outgoingReviewListProperties = {
        ...state.outgoingReviewListProperties,
        ...action.payload,
      }
    },
    setSelectedItemID: (state, action: PayloadAction<string>) => {
      state.selectedItemID = action.payload
    },
    setIsView: (state, action: PayloadAction<boolean>) => {
      state.isView = action.payload
    },
    setIsCall: (state, action: PayloadAction<boolean>) => {
      state.isCall = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getActiveCycleThunk.fulfilled, (state, action) => {
      state.activeCycle = action.payload
    })
    builder.addCase(getAllCyclesThunk.fulfilled, (state, action) => {
      state.allCycles = action.payload
    })
    builder.addCase(getEmpDepartmentsThunk.fulfilled, (state, action) => {
      state.empDepartments = action.payload
    })
    builder.addCase(getDesignationsThunk.fulfilled, (state, action) => {
      state.incomingDesignations = action.payload
    })
    builder.addCase(getReviewListThunk.fulfilled, (state, action) => {
      state.incomingReviewList = action.payload
      state.isLoading = ApiLoadingState.succeeded
    })
    builder.addMatcher(isAnyOf(getReviewListThunk.fulfilled), (state) => {
      state.isLoading = ApiLoadingState.succeeded
    })
    builder.addMatcher(isAnyOf(getReviewListThunk.pending), (state) => {
      state.isLoading = ApiLoadingState.loading
      state.error = null
    })
    builder.addMatcher(
      isAnyOf(getReviewListThunk.rejected),
      (state, action) => {
        state.isLoading = ApiLoadingState.failed
        state.error = action.payload as ValidationError
      },
    )
  },
})
const activeCycleData = (state: RootState): IncomingReviewListCycle =>
  state.reviewList.activeCycle as IncomingReviewListCycle

const reviewListTableInfo = (state: RootState): IncomingReviewList =>
  state.reviewList.incomingReviewList

const loading = (state: RootState): LoadingState => state.reviewList.isLoading

const getIsView = (state: RootState): boolean => state.reviewList.isView

const getIsCall = (state: RootState): boolean => state.reviewList.isCall

const reviewListSelectors = {
  loading,
  activeCycleData,
  reviewListTableInfo,
  getIsView,
  getIsCall,
}
const reviewListThunks = {
  getActiveCycleThunk,
  getAllCyclesThunk,
  getEmpDepartmentsThunk,
  getDesignationsThunk,
  getReviewListThunk,
}

export const reviewListServices = {
  ...reviewListThunks,
  actions: reviewListSlice.actions,
  selectors: reviewListSelectors,
}

const ReviewListReducer = reviewListSlice.reducer
export default ReviewListReducer
